import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, isApolloError, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import io from 'socket.io-client';
import Cookies from 'universal-cookie';

import { getOperationAST } from 'graphql';
import { createClient } from 'graphql-ws';
import { Kind, OperationTypeNode } from 'graphql/language';

import { scanUser } from '../pages/users/partials/log';
import { expiredUser } from '../pages/users/partials/logout';
import { reportFeLog } from './frontendLog';

const cookies = new Cookies();

const getToken = () => {
  const Retoken: string = cookies.get('token');
  return Retoken;
};

const withRetries = new RetryLink({
  delay: {
    max: 30 * 1000,
  },
  attempts: (count, operation, error) => {
    if (error && String(error).indexOf('aborted') !== -1) {
      console.log(`error ${operation.operationName} ${error}`);
      count = 0;
      return false;
    } else {
      if (count >= 1) {
        console.log(`Retrying operation ${operation.operationName} (Attempt ${count})`);
        intrigueToken();
        return false;
      }
    }

    return !!error;
  },
});

const link = createHttpLink({ uri: import.meta.env.VITE_GRAPHQL_URL });

let websocketRetryAttempt = 0;
let connectionId: string | undefined;
let connectedAt: number | undefined;
let retryAttempt = 0;

function reportWs(eventName: string, metadata: Record<string, unknown> = {}) {
  void reportFeLog({
    source: 'apollo_subscription',
    log_type: 'WS_EVENT',
    event_name: eventName,
    operation_type: 'subscription',
    metadata: {
      connectionId,
      retryAttempt,
      online: navigator.onLine,
      visibilityState: document.visibilityState,
      ...metadata,
    },
  });
}

const wsClient = createClient({
  url: import.meta.env.VITE_SUBSCRIPTION_URL,
  lazy: true,
  retryAttempts: Infinity,
  connectionParams: () => {
    const token = getToken();
    if (!token) {
      void expiredUser();
    }

    return {
      authorization: token ? `Bearer ${token}` : '',
    };
  },
  shouldRetry: () => {
    websocketRetryAttempt += 1;
    console.log(`websocket retry attempt ${websocketRetryAttempt}`);
    return true;
  },
  on: {
    connecting: (isRetry) => {
      connectionId = crypto.randomUUID();
      connectedAt = undefined;
      retryAttempt = isRetry ? retryAttempt + 1 : 0;
      reportWs(isRetry ? 'websocket_retry' : 'websocket_connecting');
    },
    connected: (socket) => {
      websocketRetryAttempt = 0;
      const activeSocket = socket as WebSocket;
      console.log('websocket connected!!', { protocol: activeSocket.protocol || null });
      connectedAt = Date.now();
      reportWs('websocket_connected', { protocol: activeSocket.protocol });
      retryAttempt = 0;
    },
    closed: (event) => {
      const closeEvent = event as CloseEvent;
      console.log('websocket closed!!', {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        willRetry: true,
      });
      reportWs('websocket_close', {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        durationMs: connectedAt == null ? undefined : Date.now() - connectedAt,
      });
      connectedAt = undefined;
    },
    error: (error) => {
      console.log('websocket error!!', error);
      reportWs('websocket_error', {
        message: error instanceof Error ? error.message : 'WebSocket transport error',
      });
    },
  },
});

const wsLink = new GraphQLWsLink(wsClient);

const intrigueToken = () => {
  const token = getToken();
  if (!token) {
    const callbackToken = () => {
      expiredUser();
    };
    scanUser()
      .then((result) => {
        if (!result) {
          callbackToken();
        }
      })
      .catch((error) => {
        console.error('Error Checking Login', error);
        callbackToken();
      });
  }
};

const forstokHttpLink = ApolloLink.from([withRetries, link]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;
  },
  wsLink,
  forstokHttpLink,
);

const forstokAuthLink = setContext((_, { headers }) => {
  const token = getToken();
  intrigueToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const errorLogLink = onError(({ graphQLErrors, networkError, operation }) => {
  const operationType = getOperationAST(operation.query, operation.operationName || undefined)?.operation;
  const common = {
    source: operationType === OperationTypeNode.SUBSCRIPTION ? 'apollo_subscription' : 'apollo_proxy',
    operation_name: operation.operationName || undefined,
    operation_type: operationType,
    variables: operation.variables,
  } as const;
  const metadata = operationType === OperationTypeNode.SUBSCRIPTION ? { connectionId } : {};

  // GraphQLWsLink wraps subscription operation errors in ApolloError.
  const subscriptionError =
    operationType === OperationTypeNode.SUBSCRIPTION && networkError && isApolloError(networkError)
      ? networkError
      : undefined;
  const errors = graphQLErrors ?? subscriptionError?.graphQLErrors ?? [];

  for (const error of errors) {
    const code = error.extensions?.code;
    void reportFeLog({
      ...common,
      log_type: 'GRAPHQL_ERROR',
      error_code: typeof code === 'string' ? code : undefined,
      error_message: error.message,
      metadata: { ...metadata, path: error.path },
    });
  }

  const transportError = subscriptionError ? subscriptionError.networkError : networkError;
  if (transportError) {
    const status = 'statusCode' in transportError ? transportError.statusCode : undefined;
    void reportFeLog({
      ...common,
      log_type: 'NETWORK_ERROR',
      error_message: transportError.message,
      status_code: typeof status === 'number' && status >= 100 && status <= 599 ? status : undefined,
      metadata,
    });
  }
});

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        orders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        order(_, { args, toReference }) {
          return toReference({
            __typename: 'Order',
            id: args?.id || '',
          });
        },
        qtyInventories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockInbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockOutbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockHistoryList: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    Warehouses: {
      keyFields: ['id'],
    },
    Accounts: {
      keyFields: ['id', ['name']],
    },
    ProductWarehouse: {
      keyFields: false,
      merge: true,
    },
    WarehouseInventoryPromotion: {
      keyFields: false,
    },
    WarehouseInventoryReservedOrder: {
      keyFields: false,
    },
  },
  addTypename: true,
});

export const client = new ApolloClient({
  link: errorLogLink.concat(forstokAuthLink).concat(splitLink),
  cache,
  queryDeduplication: false,
});

export const socket = io('wss://socket.forstok.com');
